import { IMAGE_RESIZE_SPEC } from '@core/image'
import { useI18n } from '@muse/i18n'
import {
    CollectionAddIcon,
    Button,
    DetailIcon,
    DownloadIcon,
    PencilIcon,
    ShareIcon,
    WaveformComments,
    WaveformLoader,
    type TWaveformCommentView,
} from '@ds/components'
import { sendEvent } from '@core/analytics'
import type { TAudioSimple } from '@core/models'
import { getAuthorPageUrl } from '@core/routes'
import {
    AudioPlaybackTimeContainer,
    WaveformContainer,
    sendPlayPauseClick,
    useAudioPlayback,
} from '@features/audio-player'
import { isAudioOwner } from '@features/audio'
import { AsyncDeleteAudioDialog, ReplaceSourceInput } from '@features/audio-edit'
import { AudioPlayStats, AsyncAudioActionsDialog } from '@features/audio-card'
import { LikeButtonContainer } from '@features/audio-like'
import { canDownloadAudio, useDownloadDialog } from '@features/audio-download'
import { useComments } from '@features/comment'
import { isAnonymousAuthor } from '@features/author'
import { AsyncPromoReleaseWarningDialog } from '@features/promo-page'
import { useShareDialog, type TShareAudioEntity } from '@features/share'
import { useCurrentUser } from '@features/session'
import { FollowButtonContainer } from '@features/follow'

import { AudioHero } from '../components/audio-hero/AudioHero'
import { AudioHeroPlayback } from '../components/audio-hero-playback/AudioHeroPlayback'
import { AudioHeroBar } from '../components/audio-hero-bar/AudioHeroBar'
import { HeroPlayButton } from '../components/audio-hero-play-button/HeroPlayButton'
import { HeroMobileControls } from '../components/audio-hero-mobile-controls/HeroMobileControls'
import { HeroMobileEditButton } from '../components/audio-hero-mobile-controls/HeroMobileEditButton'
import { AudioHeroMobileHeader } from '../components/audio-hero-mobile-header/AudioHeroMobileHeader'
import { AudioActionsMenu } from '../components/audio-actions-menu/AudioActionsMenu'
import { AudioHeroBottom } from '../components/audio-hero-bottom/AudioHeroBottom'
import { AUDIO_DETAILS_ANCHOR } from '../constants/anchors'
import { useAudioHeroActions } from '../hooks/useAudioHeroActions'

type TAudioHeroContainerProps = {
    audio: TAudioSimple
}

// Owns the hero's actions (share / download / like / play) and lays them out responsively: a desktop
// action bar and a centered mobile controls row (share / play / like). Waveform markers: position =
// timecode / duration; deleted + out-of-range comments dropped.
export const AudioHeroContainer = ({ audio }: TAudioHeroContainerProps) => {
    const { t, getNumber } = useI18n()
    const { isActiveAudio, isPlaying, playPause } = useAudioPlayback(audio)
    const { data: comments = [] } = useComments(audio.id)
    const download = useDownloadDialog()
    const share = useShareDialog()
    const { username: myUsername } = useCurrentUser()

    // The document already carries the owner's hero (`AudioChromeParams::isOwner`), so no gate here.
    const isOwner = isAudioOwner(audio, myUsername)
    const hero = useAudioHeroActions({ audio, isOwner })

    const isAnonymous = isAnonymousAuthor(audio.authorUsername)
    const canDownload = canDownloadAudio(audio)
    const durationSec = audio.duration / 1000

    const markers: TWaveformCommentView[] =
        durationSec > 0
            ? comments
                  .filter(
                      comment =>
                          comment.content !== '' &&
                          comment.timeInSeconds >= 0 &&
                          comment.timeInSeconds <= durationSec,
                  )
                  .map(comment => ({
                      id: comment.id,
                      positionPercent: (comment.timeInSeconds / durationSec) * 100,
                      imageUrl: comment.commenter.avatarUrl,
                      alt: `${comment.commenter.displayName} avatar`,
                      badgeCount: comment.threadCount,
                      href: getAuthorPageUrl({ authorUsername: comment.commenter.username }),
                  }))
            : []

    const shareEntity: TShareAudioEntity = {
        item: 'audio',
        id: audio.id,
        title: audio.title,
        placement: 'player_audio',
        authorUsername: audio.authorUsername,
        audioSlug: audio.slug,
    }

    const handleShare = () => {
        sendEvent({
            name: 'share_button_click',
            payload: {
                audio: audio.id,
                params__key: ['player_placement'],
                params__str_value: ['player_audio'],
            },
        })
        share.open(shareEntity)
    }

    const handleDownload = () => {
        download.open(
            {
                id: audio.id,
                title: audio.title,
                authorUsername: audio.authorUsername,
                authorId: audio.authorId,
                avatarUrl: audio.avatarUrl,
            },
            'player_audio',
        )
    }

    const handlePlayToggle = () => {
        sendPlayPauseClick(audio.id, 'player_audio', !isActiveAudio || !isPlaying)
        playPause()
    }

    const scrollToDetails = () => {
        document.getElementById(AUDIO_DETAILS_ANCHOR)?.scrollIntoView({ behavior: 'smooth' })
    }

    const playButton = (
        <HeroPlayButton
            isPlaying={isPlaying}
            onToggle={handlePlayToggle}
            playLabel={t('i18n.audioPlay')}
            pauseLabel={t('i18n.audioPause')}
        />
    )

    const shareButton = (
        <Button
            variant="secondary"
            size="medium"
            LeftIcon={ShareIcon}
            aria-label={t('i18n.audioPlayerShare')}
            onClick={handleShare}
        />
    )

    const addToCollectionButton = hero.canAddToCollection ? (
        <Button
            variant="secondary"
            size="medium"
            LeftIcon={CollectionAddIcon}
            onClick={hero.handleAddToCollection}
        />
    ) : undefined

    const downloadButton = canDownload ? (
        <Button variant="secondary" size="medium" LeftIcon={DownloadIcon} onClick={handleDownload}>
            {t('i18n.audioPlayerDownload')}
        </Button>
    ) : undefined

    const mobileDownloadButton = canDownload ? (
        <Button variant="secondary" size="large" LeftIcon={DownloadIcon} onClick={handleDownload}>
            {t('i18n.audioPlayerDownload')}
        </Button>
    ) : undefined

    const likeButton = (
        <LikeButtonContainer
            as="button"
            audioId={audio.id}
            placement="player_audio"
            likesCount={audio.likesCount}
        />
    )

    const editButton = (
        <Button
            variant="secondary"
            size="medium"
            LeftIcon={PencilIcon}
            aria-label={t('i18n.commonEdit')}
            onClick={hero.handleEdit}
        />
    )

    const followSlot = isAnonymous ? undefined : (
        <FollowButtonContainer
            username={audio.authorUsername}
            creatorUserId={audio.authorId}
            placement="player_audio"
            showOnlySecondaryVariant
            hideMobileText
        />
    )

    // The owner sees their own counters instead of a follow button, and edit + more instead of
    // like/download — download stays available inside the menu (parity with audio-web).
    const pillEndSlot = isOwner ? (
        <AudioPlayStats
            plays={{
                count: audio.playsCount,
                label: getNumber(audio.playsCount, { format: 'short' }),
            }}
            likes={{
                count: audio.likesCount,
                label: getNumber(audio.likesCount, { format: 'short' }),
            }}
        />
    ) : (
        followSlot
    )

    const actionsSlot = isOwner ? (
        <>
            {shareButton}
            {editButton}
            <AudioActionsMenu items={hero.actions} triggerLabel={t('i18n.audioMoreAudioActions')} />
        </>
    ) : (
        <>
            {shareButton}
            {addToCollectionButton}
            {downloadButton}
            {likeButton}
        </>
    )

    const ownerMobileAction =
        hero.actions.length > 0
            ? {
                  label: t('i18n.audioMoreActions'),
                  Icon: DetailIcon,
                  onClick: hero.openSheet,
                  onIntent: AsyncAudioActionsDialog.preload,
                  testID: 'audio-hero-mobile-actions',
              }
            : undefined

    const visitorMobileAction = hero.canAddToCollection
        ? {
              label: t('i18n.audioActionAddToCollection'),
              Icon: CollectionAddIcon,
              onClick: hero.handleAddToCollection,
          }
        : undefined

    const mobileHeaderSlot = (
        <AudioHeroMobileHeader action={isOwner ? ownerMobileAction : visitorMobileAction} />
    )

    const waveformSlot = hero.uploadProgressLabel ? (
        <WaveformLoader id={audio.id} variant="light" text={hero.uploadProgressLabel} />
    ) : (
        <WaveformContainer audio={audio} analyticsPlacement="player_audio" variant="light" />
    )

    const mobileTrailingSlot = isOwner ? (
        <HeroMobileEditButton label={t('i18n.commonEdit')} onClick={hero.handleEdit} />
    ) : (
        <LikeButtonContainer
            as="plain"
            audioId={audio.id}
            placement="player_audio"
            likesCount={audio.likesCount}
        />
    )

    return (
        <>
            <AudioHero
                coverSrc={audio.imageUrl}
                title={audio.title}
                playbackSlot={
                    <AudioHeroPlayback
                        timeSlot={
                            <AudioPlaybackTimeContainer
                                audioId={audio.id}
                                durationMs={audio.duration}
                            />
                        }
                        playButtonSlot={playButton}
                        waveformSlot={waveformSlot}
                        overlaySlot={
                            <WaveformComments comments={markers} resize={IMAGE_RESIZE_SPEC} />
                        }
                    />
                }
                mobileHeaderSlot={mobileHeaderSlot}
                mobileControlsSlot={
                    <HeroMobileControls
                        shareLabel={t('i18n.audioPlayerShare')}
                        onShare={handleShare}
                        playSlot={playButton}
                        trailingSlot={mobileTrailingSlot}
                    />
                }
                mobileDownloadSlot={mobileDownloadButton}
                heroBarSlot={
                    <AudioHeroBar
                        authorName={audio.authorDisplayName}
                        authorHref={
                            isAnonymous
                                ? undefined
                                : getAuthorPageUrl({ authorUsername: audio.authorUsername })
                        }
                        avatarUrl={audio.avatarUrl}
                        pillEndSlot={pillEndSlot}
                        actionsSlot={actionsSlot}
                    />
                }
                bottomSlot={
                    <AudioHeroBottom
                        label={t('i18n.audioDetailsAndComments')}
                        onScrollToDetails={scrollToDetails}
                    />
                }
            />
            {isOwner ? (
                <>
                    <ReplaceSourceInput
                        ref={hero.fileInputRef}
                        onSelectFile={hero.onSelectSourceFile}
                        testID={`audio-${audio.id}-replace-source-input`}
                    />
                    <AsyncAudioActionsDialog
                        open={hero.isSheetOpen}
                        onOpenChange={hero.setIsSheetOpen}
                        title={t('i18n.audioActionsSheetTitle')}
                        cancelLabel={t('i18n.commonCancel')}
                        items={hero.actions}
                    />
                    <AsyncPromoReleaseWarningDialog
                        open={hero.isPromoWarningOpen}
                        onOpenChange={hero.setIsPromoWarningOpen}
                        onContinue={hero.openDeleteAfterPromoWarning}
                        title={t('i18n.audioPromoReleaseDeleteTitle')}
                        description={hero.promoWarningDescription}
                        continueLabel={t('i18n.commonContinue')}
                        cancelLabel={t('i18n.commonCancel')}
                    />
                    <AsyncDeleteAudioDialog
                        open={hero.isDeleteOpen}
                        onOpenChange={hero.setIsDeleteOpen}
                        onConfirm={hero.confirmDelete}
                        isDeleting={hero.isDeleting}
                        title={t('i18n.audioDeleteDialogTitle')}
                        description={audio.title}
                        confirmLabel={t('i18n.commonDelete')}
                        deletingLabel={t('i18n.commonDeleting')}
                        cancelLabel={t('i18n.commonCancel')}
                    />
                </>
            ) : null}
        </>
    )
}
